import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { 
  ArrowLeft, 
  Crown, 
  Smartphone, 
  Eye, 
  Clock,
  CheckCircle,
  PlayCircle,
  XCircle,
  RotateCcw,
  Shield,
  AlertTriangle,
  RefreshCw,
  Edit3,
  X,
  Save,
  Image,
  Camera,
  Upload,
  Pause,
  Play,
  Wrench
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IssueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canAcknowledge, canResolve, canClose, canReopen, canOverrideSLA, canEditSeverity } = useAuth();
  
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [preventiveAction, setPreventiveAction] = useState("");
  const [departmentActionPlan, setDepartmentActionPlan] = useState({});
  const [actionPlanImages, setActionPlanImages] = useState({});
  const [uploadingActionImage, setUploadingActionImage] = useState(false);
  const [acknowledgeRemark, setAcknowledgeRemark] = useState({});
  const [picInput, setPicInput] = useState({});
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editImages, setEditImages] = useState([]);
  const [uploadingEditImage, setUploadingEditImage] = useState(false);
  
  // Pending state
  const [showPendingDialog, setShowPendingDialog] = useState(false);
  const [pendingReason, setPendingReason] = useState("");
  const [pendingNote, setPendingNote] = useState("");
  const [pendingLoading, setPendingLoading] = useState(false);
  
  // Image view state
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchIssue = async () => {
    try {
      const res = await axios.get(`${API}/issues/${id}`);
      setIssue(res.data);
    } catch (err) {
      toast.error("Failed to load issue");
      navigate("/issues");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssue();
  }, [id]);

  const handleAction = async (action, data = {}) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/issues/${id}/${action}`, data);
      toast.success(`Issue ${action} successfully`);
      fetchIssue();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Department-specific acknowledge
  const handleDeptAcknowledge = async (deptCode) => {
    setActionLoading(true);
    try {
      const remark = acknowledgeRemark[deptCode] || '';
      await axios.post(`${API}/issues/${id}/department/${deptCode}/acknowledge`, { remark });
      toast.success(`Department ${deptCode} acknowledged`);
      fetchIssue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to acknowledge");
    } finally {
      setActionLoading(false);
    }
  };

  // Department-specific resolve with action plan
  const handleDeptResolve = async (deptCode) => {
    const actionPlan = departmentActionPlan[deptCode];
    if (!actionPlan) {
      toast.error("Action plan is required");
      return;
    }
    setActionLoading(true);
    try {
      const images = actionPlanImages[deptCode] || [];
      await axios.post(`${API}/issues/${id}/department/${deptCode}/resolve`, { 
        action_plan: actionPlan,
        action_images: images
      });
      toast.success(`Department ${deptCode} resolved`);
      fetchIssue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resolve");
    } finally {
      setActionLoading(false);
    }
  };

  // Assign (or reassign) the PIC (person in charge) manually handling a department's defect
  const handleAssignPic = async (deptCode) => {
    const current = picInput[deptCode] ?? issue.department_status?.[deptCode]?.pic_name ?? '';
    const picName = current.trim();
    if (!picName) {
      toast.error("PIC name is required");
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/issues/${id}/department/${deptCode}/assign-pic`, { pic_name: picName });
      toast.success(`PIC assigned to ${deptCode}`);
      setPicInput(prev => ({ ...prev, [deptCode]: undefined }));
      fetchIssue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign PIC");
    } finally {
      setActionLoading(false);
    }
  };

  // Upload action plan image
  const handleActionImageUpload = async (deptCode, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingActionImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/image`, formData);
      setActionPlanImages(prev => ({
        ...prev,
        [deptCode]: [...(prev[deptCode] || []), res.data.url]
      }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingActionImage(false);
    }
  };

  // Close appreciation issue
  const handleCloseAppreciation = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/issues/${id}/close-appreciation`);
      toast.success("Appreciation closed");
      fetchIssue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to close appreciation");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/issues/${id}/resolve`, {
        resolution_notes: resolutionNotes,
        root_cause_category: rootCause || null,
        preventive_action: preventiveAction || null,
      });
      toast.success("Issue resolved");
      fetchIssue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resolve");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSeverityChange = async (severity) => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/issues/${id}/severity?severity=${severity}`);
      toast.success("Severity updated");
      fetchIssue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update severity");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetPending = async () => {
    if (!pendingReason) {
      toast.error("Please select a reason");
      return;
    }
    setPendingLoading(true);
    try {
      await axios.post(`${API}/issues/${id}/pending`, {
        reason: pendingReason,
        note: pendingNote || null
      });
      toast.success("Issue set to pending");
      setShowPendingDialog(false);
      setPendingReason("");
      setPendingNote("");
      fetchIssue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set pending");
    } finally {
      setPendingLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/issues/${id}/resume`);
      toast.success("Issue resumed");
      fetchIssue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resume");
    } finally {
      setActionLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      S4: "bg-red-500/10 text-red-500 border-red-500/20",
      S3: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      S2: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      S1: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      P1: "bg-purple-500/10 text-purple-500 border-purple-500/20",
    };
    return colors[severity] || colors.S2;
  };

  const getStatusColor = (status) => {
    const colors = {
      OPEN: "bg-red-500/10 text-red-500 border-red-500/20",
      ACKNOWLEDGED: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      IN_PROGRESS: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      PENDING: "bg-purple-500/10 text-purple-500 border-purple-500/20",
      RESOLVED: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      CLOSED: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
    };
    return colors[status] || colors.OPEN;
  };

  const PENDING_REASONS = [
    { value: "WAITING_PURCHASE", label: "Waiting Purchase" },
    { value: "WAITING_VENDOR", label: "Waiting Vendor" },
    { value: "WAITING_APPROVAL", label: "Waiting Approval" },
    { value: "WAITING_MATERIAL", label: "Waiting Material" },
    { value: "WAITING_BUDGET", label: "Waiting Budget" },
    { value: "OTHER", label: "Other" },
  ];

  const getRiskColor = (level) => {
    const colors = {
      CRITICAL: "text-red-500 bg-red-500/10 border-red-500/20",
      HIGH: "text-amber-500 bg-amber-500/10 border-amber-500/20",
      MEDIUM: "text-blue-500 bg-blue-500/10 border-blue-500/20",
      LOW: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
    };
    return colors[level] || "text-muted-foreground";
  };

  const getGuestStatusColor = (status) => {
    const colors = {
      ARRIVAL: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      IN_HOUSE: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      DEPARTURE: "bg-amber-500/10 text-amber-400 border-amber-500/20",
      OUTSIDER: "bg-gray-500/10 text-gray-400 border-gray-500/20",
    };
    return colors[status] || colors.IN_HOUSE;
  };

  const getGuestStatusLabel = (status) => {
    const labels = { 
      ARRIVAL: "Arrival", 
      IN_HOUSE: "In-House", 
      DEPARTURE: "Departure",
      OUTSIDER: "Outsider"
    };
    return labels[status] || status;
  };

  // Department code to full name mapping
  const getDepartmentLabel = (code) => {
    const labels = {
      FO: "Front Office (Resepsionis)",
      HK: "Housekeeping (Tata Graha)",
      ENG: "Engineering (Teknik)",
      FNB: "Food & Beverage (Restoran)",
      SEC: "Security (Keamanan)",
      IT: "Information Technology",
      MGT: "Management"
    };
    return labels[code] || code;
  };

  // Short department label for badges
  const getDepartmentShortLabel = (code) => {
    const labels = {
      FO: "Front Office",
      HK: "Housekeeping",
      ENG: "Engineering",
      FNB: "F&B",
      SEC: "Security",
      IT: "IT",
      MGT: "Management"
    };
    return labels[code] || code;
  };

  const handleReanalyze = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/reanalyze-issue/${id}`);
      toast.success(`Issue re-analyzed. New risk score: ${res.data.new_values.risk_score}`);
      fetchIssue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to re-analyze");
    } finally {
      setActionLoading(false);
    }
  };

  // Start edit mode
  const handleStartEdit = () => {
    setEditData({
      room_number: issue.room_number || "",
      guest_name: issue.guest_name || "",
      guest_type: issue.guest_type || "REGULAR",
      guest_status: issue.guest_status || "IN_HOUSE",
      defect_type: issue.defect_type || "GUEST_DEFECT",
      booking_source: issue.booking_source || "Walk-in",
      check_in_date: issue.check_in_date ? issue.check_in_date.split("T")[0] : "",
      check_out_date: issue.check_out_date ? issue.check_out_date.split("T")[0] : "",
      description: issue.description || "",
    });
    setEditImages(issue.images || []);
    setIsEditing(true);
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({});
    setEditImages([]);
  };

  // Upload image for edit mode
  const handleEditImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingEditImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload/image`, formData);
      setEditImages(prev => [...prev, res.data.url]);
      toast.success("Foto berhasil diupload");
    } catch (err) {
      toast.error("Gagal upload foto");
    } finally {
      setUploadingEditImage(false);
      e.target.value = "";
    }
  };

  // Remove image in edit mode
  const handleRemoveEditImage = (index) => {
    setEditImages(prev => prev.filter((_, i) => i !== index));
  };

  // Save edited data
  const handleSaveEdit = async () => {
    if (!editData.description?.trim()) {
      toast.error("Description cannot be empty");
      return;
    }
    
    setEditLoading(true);
    try {
      await axios.put(`${API}/issues/${id}`, {
        room_number: editData.room_number,
        guest_name: editData.guest_name,
        guest_type: editData.guest_type,
        guest_status: editData.guest_status,
        defect_type: editData.defect_type,
        booking_source: editData.booking_source,
        check_in_date: editData.check_in_date ? new Date(editData.check_in_date).toISOString() : null,
        check_out_date: editData.check_out_date ? new Date(editData.check_out_date).toISOString() : null,
        description: editData.description,
        images: editImages,
      });
      toast.success("Issue updated successfully");
      setIsEditing(false);
      setEditImages([]);
      fetchIssue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update issue");
    } finally {
      setEditLoading(false);
    }
  };

  const getTimelineIcon = (action) => {
    const icons = {
      CREATED: <AlertTriangle size={14} />,
      ACKNOWLEDGED: <Eye size={14} />,
      IN_PROGRESS: <PlayCircle size={14} />,
      RESOLVED: <CheckCircle size={14} />,
      CLOSED: <XCircle size={14} />,
      REOPENED: <RotateCcw size={14} />,
      SEVERITY_CHANGED: <Shield size={14} />,
    };
    return icons[action] || <Clock size={14} />;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!issue) return null;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/issues")} data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-mono text-2xl font-bold text-primary">
                {issue.issue_number}
              </h1>
              <Badge className={getSeverityColor(issue.final_severity)}>
                {issue.final_severity}
              </Badge>
              <Badge className={getStatusColor(issue.status)}>
                {issue.status}
              </Badge>
              {issue.status === "PENDING" && issue.pending_reason && (
                <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                  {issue.pending_reason.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
              )}
              {issue.guest_type === "VIP" && (
                <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                  <Crown size={12} className="mr-1" /> VIP
                </Badge>
              )}
              {issue.guest_status && (
                <Badge className={getGuestStatusColor(issue.guest_status)}>
                  {getGuestStatusLabel(issue.guest_status)}
                </Badge>
              )}
              {issue.severity_locked && (
                <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                  Locked
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Room {issue.room_number} • {issue.guest_name} • {issue.department_owner}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Issue Details */}
            <Card className="orbit-card" data-testid="issue-details">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-sm uppercase tracking-wider">
                  Issue Details
                </CardTitle>
                {!isEditing && issue.status !== "CLOSED" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleStartEdit}
                    className="text-muted-foreground hover:text-primary"
                    data-testid="edit-description-btn"
                  >
                    <Edit3 size={14} className="mr-1" />
                    Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <div className="space-y-4">
                    {/* Edit Form */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Room Number</p>
                        <Input
                          value={editData.room_number}
                          onChange={(e) => setEditData({ ...editData, room_number: e.target.value })}
                          className="bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Guest Name</p>
                        <Input
                          value={editData.guest_name}
                          onChange={(e) => setEditData({ ...editData, guest_name: e.target.value })}
                          className="bg-background"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Guest Type</p>
                        <Select value={editData.guest_type} onValueChange={(v) => setEditData({ ...editData, guest_type: v })}>
                          <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="REGULAR">Regular</SelectItem>
                            <SelectItem value="VIP">VIP</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Guest Status</p>
                        <Select value={editData.guest_status} onValueChange={(v) => setEditData({ ...editData, guest_status: v })}>
                          <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ARRIVAL">Arrival</SelectItem>
                            <SelectItem value="IN_HOUSE">In-House</SelectItem>
                            <SelectItem value="DEPARTURE">Departure</SelectItem>
                            <SelectItem value="OUTSIDER">Outsider</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Defect Type</p>
                        <Select value={editData.defect_type} onValueChange={(v) => setEditData({ ...editData, defect_type: v })}>
                          <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GUEST_DEFECT">Guest Defect</SelectItem>
                            <SelectItem value="DMS_LOG">DMS Log</SelectItem>
                            <SelectItem value="MOD">MOD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Booking Source</p>
                        <Input
                          value={editData.booking_source}
                          onChange={(e) => setEditData({ ...editData, booking_source: e.target.value })}
                          className="bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Check In</p>
                        <Input
                          type="date"
                          value={editData.check_in_date}
                          onChange={(e) => setEditData({ ...editData, check_in_date: e.target.value })}
                          className="bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Check Out</p>
                        <Input
                          type="date"
                          value={editData.check_out_date}
                          onChange={(e) => setEditData({ ...editData, check_out_date: e.target.value })}
                          className="bg-background"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">Description</p>
                      <Textarea
                        value={editData.description}
                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                        rows={5}
                        className="bg-background resize-none"
                      />
                    </div>
                    
                    {/* Edit Images Section */}
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                        <Camera size={12} /> Foto ({editImages.length})
                      </p>
                      <div className="grid grid-cols-4 gap-2">
                        {editImages.map((url, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                              alt={`Issue photo ${index + 1}`}
                              className="w-full h-20 object-cover rounded border border-border"
                              onError={(e) => { 
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="80" viewBox="0 0 100 80"><rect fill="%23374151" width="100" height="80"/><text x="50%" y="50%" fill="%239CA3AF" font-size="10" text-anchor="middle" dy=".3em">No Image</text></svg>';
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveEditImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                        {/* Add New Image Button */}
                        <label className="w-full h-20 border-2 border-dashed border-border rounded flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleEditImageUpload}
                            className="hidden"
                            disabled={uploadingEditImage}
                          />
                          {uploadingEditImage ? (
                            <RefreshCw size={16} className="animate-spin text-muted-foreground" />
                          ) : (
                            <>
                              <Upload size={16} className="text-muted-foreground" />
                              <span className="text-xs text-muted-foreground mt-1">Add</span>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button onClick={handleSaveEdit} disabled={editLoading} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                        {editLoading ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                        Simpan
                      </Button>
                      <Button onClick={handleCancelEdit} disabled={editLoading} variant="outline" size="sm">
                        <X size={14} className="mr-1" /> Batal
                      </Button>
                    </div>
                  </div>
                ) : (
                <div className="space-y-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    Description
                  </p>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {issue.description}
                    </p>
                </div>
                
                {/* Display Images */}
                {issue.images && issue.images.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                      <Camera size={12} /> Foto Temuan ({issue.images.length})
                    </p>
                    <div className="grid grid-cols-4 gap-2">
                      {issue.images.map((url, index) => (
                        <div
                          key={index}
                          className="relative cursor-pointer group"
                          onClick={() => setSelectedImage(`${process.env.REACT_APP_BACKEND_URL}${url}`)}
                        >
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                            alt={`Issue photo ${index + 1}`}
                            className="w-full h-20 object-cover rounded border border-border group-hover:border-primary transition-colors"
                            onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="w-full h-20 bg-muted/50 rounded border border-border flex items-center justify-center text-xs text-muted-foreground">Image not available</div>'; }}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded transition-colors flex items-center justify-center">
                            <Eye size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      Category
                    </p>
                    <p className="text-sm">{issue.category.replace(/_/g, " ")}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      Department
                    </p>
                    <p className="text-sm">{getDepartmentLabel(issue.department_owner)}</p>
                  </div>
                </div>
                {issue.praised_departments?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      Praised Departments
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {issue.praised_departments.map((dept) => (
                        <Badge key={dept} className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20" title={getDepartmentLabel(dept)}>
                          {getDepartmentShortLabel(dept)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {issue.defect_departments?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      Defect Departments
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {issue.defect_departments.map((dept) => (
                        <Badge key={dept} className="bg-red-500/10 text-red-500 border-red-500/20" title={getDepartmentLabel(dept)}>
                          {getDepartmentShortLabel(dept)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {/* Issue Type Badge */}
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    Issue Type
                  </p>
                  {(() => {
                    // Calculate issue type from existing data if not set
                    const hasPraised = issue.praised_departments?.length > 0;
                    const hasDefect = issue.defect_departments?.length > 0;
                    const calculatedType = issue.issue_type || (
                      hasPraised && hasDefect ? "MIXED" :
                      hasPraised ? "APPRECIATION" :
                      "DEFECT"
                    );
                    return (
                      <Badge className={
                        calculatedType === "APPRECIATION" ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" :
                        calculatedType === "MIXED" ? "bg-amber-500/10 text-amber-500 border-amber-500/20" :
                        "bg-red-500/10 text-red-500 border-red-500/20"
                      }>
                        {calculatedType}
                      </Badge>
                    );
                  })()}
                </div>
                {issue.resolution_notes && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      Resolution Notes
                    </p>
                    <p className="text-sm">{issue.resolution_notes}</p>
                  </div>
                )}
                </div>
                )}
              </CardContent>
            </Card>

            {/* Department Status - Per Department Actions */}
            {(issue.praised_departments?.length > 0 || issue.defect_departments?.length > 0) && (
              <Card className="orbit-card" data-testid="department-status">
                <CardHeader>
                  <CardTitle className="font-heading text-sm uppercase tracking-wider">
                    Department Status
                  </CardTitle>
                  {issue.status === "CLOSED" && (
                    <p className="text-xs text-muted-foreground">Issue sudah Closed. Catatan dan foto penyelesaian tetap tersedia sebagai riwayat (hanya baca).</p>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Praised Departments */}
                  {issue.praised_departments?.map((dept) => {
                    const deptStatus = issue.department_status?.[dept] || { status: 'PENDING' };
                    const isMyDept = user?.department === dept;
                    const canActOnDept = issue.status !== 'CLOSED' && (user?.role === 'GM' || user?.role === 'DM' || isMyDept);
                    
                    return (
                      <div key={dept} className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                              PRAISED
                            </Badge>
                            <span className="font-medium">{getDepartmentShortLabel(dept)}</span>
                          </div>
                          <Badge className={
                            deptStatus.status === 'ACKNOWLEDGED' ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" :
                            "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"
                          }>
                            {deptStatus.status}
                          </Badge>
                        </div>
                        {deptStatus.acknowledged_by && (
                          <p className="text-xs text-muted-foreground mb-2">
                            Acknowledged by {deptStatus.acknowledged_by}
                          </p>
                        )}
                        {deptStatus.status === 'PENDING' && canActOnDept && (
                          <Button
                            onClick={() => handleDeptAcknowledge(dept)}
                            disabled={actionLoading}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`acknowledge-${dept}-btn`}
                          >
                            <Eye size={14} className="mr-1" />
                            Acknowledge
                          </Button>
                        )}
                      </div>
                    );
                  })}

                  {/* Defect Departments */}
                  {issue.defect_departments?.map((dept) => {
                    const deptStatus = issue.department_status?.[dept] || { status: 'PENDING' };
                    const isMyDept = user?.department === dept;
                    const canActOnDept = issue.status !== 'CLOSED' && (user?.role === 'GM' || user?.role === 'DM' || isMyDept);
                    
                    return (
                      <div key={dept} className="p-4 bg-red-500/5 border border-red-500/20 rounded-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-red-500/10 text-red-500 border-red-500/20">
                              DEFECT
                            </Badge>
                            <span className="font-medium">{getDepartmentShortLabel(dept)}</span>
                          </div>
                          <Badge className={
                            deptStatus.status === 'RESOLVED' ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" :
                            deptStatus.status === 'ACKNOWLEDGED' ? "bg-amber-500/10 text-amber-500 border-amber-500/20" :
                            "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"
                          }>
                            {deptStatus.status}
                          </Badge>
                        </div>
                        {deptStatus.acknowledged_by && (
                          <p className="text-xs text-muted-foreground">
                            Acknowledged by {deptStatus.acknowledged_by}
                          </p>
                        )}

                        {/* Assigned To (PIC) */}
                        <div className="mt-3 pt-3 border-t border-dashed border-border">
                          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                            Assigned To (PIC)
                          </p>
                          {canActOnDept ? (
                            <div className="space-y-2">
                              {deptStatus.pic_name && (
                                <p className="text-xs text-muted-foreground">
                                  Currently: <span className="text-foreground font-medium">{deptStatus.pic_name}</span>
                                  {deptStatus.pic_assigned_by && ` — assigned by ${deptStatus.pic_assigned_by}`}
                                </p>
                              )}
                              <div className="flex gap-2">
                                <Input
                                  placeholder="e.g. Budi Santoso"
                                  value={picInput[dept] ?? deptStatus.pic_name ?? ''}
                                  onChange={(e) => setPicInput(prev => ({ ...prev, [dept]: e.target.value }))}
                                  className="bg-background text-sm"
                                  data-testid={`pic-input-${dept}`}
                                />
                                <Button
                                  onClick={() => handleAssignPic(dept)}
                                  disabled={actionLoading}
                                  size="sm"
                                  variant="outline"
                                  data-testid={`assign-pic-${dept}-btn`}
                                >
                                  <Wrench size={14} className="mr-1" />
                                  {deptStatus.pic_name ? "Reassign" : "Assign"}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm">
                              {deptStatus.pic_name || (
                                <span className="text-muted-foreground">Not yet assigned</span>
                              )}
                            </p>
                          )}
                        </div>

                        {(deptStatus.action_plan || deptStatus.action_images?.length > 0 || deptStatus.resolved_by || deptStatus.resolved_at) && (
                          <div className="mt-2 p-2 bg-background/50 rounded">
                            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Catatan Penyelesaian / Action Plan</p>
                            {deptStatus.action_plan && <p className="text-sm whitespace-pre-wrap">{deptStatus.action_plan}</p>}
                            {deptStatus.action_images && deptStatus.action_images.length > 0 && (
                              <>
                              <p className="text-xs text-muted-foreground mt-2">Foto Penyelesaian ({deptStatus.action_images.length})</p>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {deptStatus.action_images.map((url, idx) => (
                                  <img 
                                    key={idx} 
                                    src={`${process.env.REACT_APP_BACKEND_URL}${url}`} 
                                    alt={`Foto penyelesaian ${dept} ${idx+1}`}
                                    className="w-20 h-20 object-cover rounded border cursor-pointer hover:border-primary"
                                    onClick={() => setSelectedImage(`${process.env.REACT_APP_BACKEND_URL}${url}`)}
                                  />
                                ))}
                              </div>
                              </>
                            )}
                            {deptStatus.resolved_by && (
                              <p className="text-xs text-muted-foreground mt-1">Resolved by {deptStatus.resolved_by}</p>
                            )}
                            {deptStatus.resolved_at && (
                              <p className="text-xs text-muted-foreground mt-1">Waktu penyelesaian: {new Date(deptStatus.resolved_at).toLocaleString('id-ID')}</p>
                            )}
                          </div>
                        )}
                        {canActOnDept && deptStatus.status === 'PENDING' && (
                          <div className="mt-3 space-y-2">
                            <Input
                              placeholder="Remark (optional)..."
                              value={acknowledgeRemark[dept] || ''}
                              onChange={(e) => setAcknowledgeRemark(prev => ({...prev, [dept]: e.target.value}))}
                              className="bg-background text-sm"
                            />
                            <Button
                              onClick={() => handleDeptAcknowledge(dept)}
                              disabled={actionLoading}
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700"
                              data-testid={`acknowledge-${dept}-btn`}
                            >
                              <Eye size={14} className="mr-1" />
                              Acknowledge
                            </Button>
                          </div>
                        )}
                        {canActOnDept && (deptStatus.status === 'PENDING' || deptStatus.status === 'ACKNOWLEDGED') && (
                          <div className="mt-3 space-y-2">
                            <Textarea
                              placeholder="Enter action plan / resolution for this department..."
                              value={departmentActionPlan[dept] || ''}
                              onChange={(e) => setDepartmentActionPlan(prev => ({...prev, [dept]: e.target.value}))}
                              className="bg-background text-sm"
                              rows={2}
                              data-testid={`action-plan-${dept}`}
                            />
                            {/* Action Plan Images */}
                            <div className="flex flex-wrap gap-2 items-center">
                              {(actionPlanImages[dept] || []).map((url, idx) => (
                                <img 
                                  key={idx} 
                                  src={`${process.env.REACT_APP_BACKEND_URL}${url}`} 
                                  alt={`Progress ${idx+1}`}
                                  className="w-16 h-16 object-cover rounded border cursor-pointer"
                                  onClick={() => setSelectedImage(`${process.env.REACT_APP_BACKEND_URL}${url}`)}
                                />
                              ))}
                              <label className="flex items-center gap-1 px-2 py-1 text-xs bg-surface border border-border rounded cursor-pointer hover:bg-surface-highlight">
                                <Upload size={12} />
                                {uploadingActionImage ? 'Uploading...' : 'Add Photo'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handleActionImageUpload(dept, e)}
                                  disabled={uploadingActionImage}
                                />
                              </label>
                            </div>
                            <Button
                              onClick={() => handleDeptResolve(dept)}
                              disabled={actionLoading || !departmentActionPlan[dept]}
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`resolve-${dept}-btn`}
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Resolve with Action Plan
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Close Appreciation Button */}
                  {(() => {
                    // Calculate issue type from existing data if not set
                    const hasPraised = issue.praised_departments?.length > 0;
                    const hasDefect = issue.defect_departments?.length > 0;
                    const calculatedType = issue.issue_type || (
                      hasPraised && hasDefect ? "MIXED" :
                      hasPraised ? "APPRECIATION" :
                      "DEFECT"
                    );
                    
                    if (calculatedType === 'APPRECIATION' && issue.status !== 'CLOSED') {
                      return (
                        <div className="pt-4 border-t border-border">
                          <Button
                            onClick={handleCloseAppreciation}
                            disabled={actionLoading}
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                            data-testid="close-appreciation-btn"
                          >
                            <CheckCircle size={16} className="mr-2" />
                            Close Appreciation
                          </Button>
                          <p className="text-xs text-muted-foreground mt-2 text-center">
                            Semua department yang dipuji harus acknowledge sebelum bisa di-close
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            {issue.status !== "CLOSED" && (
              <Card className="orbit-card" data-testid="issue-actions">
                <CardHeader>
                  <CardTitle className="font-heading text-sm uppercase tracking-wider">
                    Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Acknowledge */}
                  {issue.status === "OPEN" && canAcknowledge() && (
                    <Button
                      onClick={() => handleAction("acknowledge")}
                      disabled={actionLoading}
                      className="w-full btn-primary"
                      data-testid="acknowledge-btn"
                    >
                      <Eye size={16} className="mr-2" />
                      Acknowledge Issue
                    </Button>
                  )}

                  {/* Progress */}
                  {issue.status === "ACKNOWLEDGED" && canResolve() && (
                    <Button
                      onClick={() => handleAction("progress")}
                      disabled={actionLoading}
                      variant="outline"
                      className="w-full"
                      data-testid="progress-btn"
                    >
                      <PlayCircle size={16} className="mr-2" />
                      Set In Progress
                    </Button>
                  )}

                  {/* Resolve */}
                  {(issue.status === "ACKNOWLEDGED" || issue.status === "IN_PROGRESS") && canResolve() && (
                    <div className="space-y-3 p-4 bg-surface-highlight/50 rounded-sm">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">
                        Resolution
                      </p>
                      <Textarea
                        placeholder="Enter resolution notes..."
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        className="bg-background"
                        data-testid="resolution-notes"
                      />
                      <Select value={rootCause} onValueChange={setRootCause}>
                        <SelectTrigger className="bg-background" data-testid="root-cause-select">
                          <SelectValue placeholder="Root Cause Category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="HUMAN">Human Error</SelectItem>
                          <SelectItem value="PROCESS">Process Issue</SelectItem>
                          <SelectItem value="SYSTEM">System Failure</SelectItem>
                          <SelectItem value="VENDOR">Vendor Issue</SelectItem>
                          <SelectItem value="ASSET">Asset Problem</SelectItem>
                        </SelectContent>
                      </Select>
                      <Textarea
                        placeholder="Preventive action..."
                        value={preventiveAction}
                        onChange={(e) => setPreventiveAction(e.target.value)}
                        className="bg-background"
                        data-testid="preventive-action"
                      />
                      <Button
                        onClick={handleResolve}
                        disabled={actionLoading || !resolutionNotes}
                        className="w-full btn-primary"
                        data-testid="resolve-btn"
                      >
                        <CheckCircle size={16} className="mr-2" />
                        Resolve Issue
                      </Button>
                    </div>
                  )}

                  {/* Set Pending - available from OPEN, ACKNOWLEDGED, IN_PROGRESS */}
                  {["OPEN", "ACKNOWLEDGED", "IN_PROGRESS"].includes(issue.status) && canResolve() && (
                    <Button
                      onClick={() => setShowPendingDialog(true)}
                      disabled={actionLoading}
                      variant="outline"
                      className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                      data-testid="set-pending-btn"
                    >
                      <Pause size={16} className="mr-2" />
                      Set Pending
                    </Button>
                  )}

                  {/* Resume from Pending */}
                  {issue.status === "PENDING" && canResolve() && (
                    <Button
                      onClick={handleResume}
                      disabled={actionLoading}
                      className="w-full btn-primary"
                      data-testid="resume-btn"
                    >
                      <Play size={16} className="mr-2" />
                      Resume Issue
                    </Button>
                  )}

                  {/* Close */}
                  {issue.status === "RESOLVED" && canClose(issue) && (
                    <Button
                      onClick={() => handleAction("close")}
                      disabled={actionLoading}
                      variant="outline"
                      className="w-full"
                      data-testid="close-btn"
                    >
                      <XCircle size={16} className="mr-2" />
                      Close Issue
                    </Button>
                  )}

                  {/* Severity Edit (DM/GM only) */}
                  {canEditSeverity() && (
                    <div className="p-4 bg-surface-highlight/50 rounded-sm">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                        Change Severity
                      </p>
                      <Select onValueChange={handleSeverityChange} data-testid="severity-select">
                        <SelectTrigger className="bg-background">
                          <SelectValue placeholder={issue.final_severity} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="S1">S1 - Low</SelectItem>
                          <SelectItem value="S2">S2 - Medium</SelectItem>
                          <SelectItem value="S3">S3 - High</SelectItem>
                          <SelectItem value="S4">S4 - Critical</SelectItem>
                          <SelectItem value="P1">P1 - Praise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* GM Special Actions */}
            {(user?.role === "GM" || user?.role === "DM") && (
              <Card className="orbit-card border-amber-500/30" data-testid="gm-actions">
                <CardHeader>
                  <CardTitle className="font-heading text-sm uppercase tracking-wider text-amber-500">
                    {user?.role === "GM" ? "GM Actions" : "DM Actions"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-3">
                  {/* Re-Analyze with AI */}
                  <Button
                    onClick={handleReanalyze}
                    disabled={actionLoading}
                    variant="outline"
                    className="border-blue-500/30 text-blue-500 hover:bg-blue-500/10"
                    data-testid="reanalyze-btn"
                  >
                    <RefreshCw size={16} className="mr-2" />
                    Re-Analyze AI
                  </Button>
                  {issue.status === "CLOSED" && canReopen() && (
                    <Button
                      onClick={() => handleAction("reopen")}
                      disabled={actionLoading}
                      variant="outline"
                      className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                      data-testid="reopen-btn"
                    >
                      <RotateCcw size={16} className="mr-2" />
                      Reopen Issue
                    </Button>
                  )}
                  {issue.sla_breached && !issue.is_sla_overridden && canOverrideSLA() && (
                    <Button
                      onClick={() => handleAction("override-sla")}
                      disabled={actionLoading}
                      variant="outline"
                      className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                      data-testid="override-sla-btn"
                    >
                      <Shield size={16} className="mr-2" />
                      Override SLA
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Risk Metrics */}
            <Card className="orbit-card" data-testid="risk-metrics">
              <CardHeader>
                <CardTitle className="font-heading text-sm uppercase tracking-wider">
                  Risk Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className={`p-4 rounded-sm border ${getRiskColor(issue.risk_level)}`}>
                  <p className="text-xs uppercase tracking-wider opacity-70">Risk Score</p>
                  <p className="font-heading text-4xl font-bold">{issue.risk_score}</p>
                  <p className="text-xs uppercase tracking-wider mt-1">{issue.risk_level}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    Sentiment
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-xl">{issue.sentiment_score?.toFixed(1)}</span>
                    <Badge className={
                      issue.sentiment_label === "POSITIVE" ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" :
                      issue.sentiment_label === "NEGATIVE" ? "bg-red-500/10 text-red-500 border-red-500/20" :
                      "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"
                    }>
                      {issue.sentiment_label}
                    </Badge>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      SLA Response
                    </p>
                    <p className="text-sm">{issue.sla_response_minutes} min</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      SLA Resolution
                    </p>
                    <p className="text-sm">{issue.sla_resolution_minutes} min</p>
                  </div>
                </div>
                {issue.sla_breached && (
                  <Badge className="bg-red-500/10 text-red-500 border-red-500/20 w-full justify-center">
                    SLA Breached {issue.is_sla_overridden && "(Overridden)"}
                  </Badge>
                )}
                {issue.is_recurring && (
                  <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 w-full justify-center">
                    Recurring Issue ({issue.recurring_count} times)
                  </Badge>
                )}
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card className="orbit-card" data-testid="timeline">
              <CardHeader>
                <CardTitle className="font-heading text-sm uppercase tracking-wider">
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {issue.timeline?.map((event, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-surface-highlight flex items-center justify-center flex-shrink-0">
                        {getTimelineIcon(event.action)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{event.action.replace(/_/g, " ")}</p>
                        <p className="text-xs text-muted-foreground">{event.by}</p>
                        <p className="font-mono text-[10px] text-muted-foreground">
                          {new Date(event.at).toLocaleString()}
                        </p>
                        {event.note && (
                          <p className="text-xs text-muted-foreground mt-1">{event.note}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Timestamps */}
            <Card className="orbit-card">
              <CardHeader>
                <CardTitle className="font-heading text-sm uppercase tracking-wider">
                  Timestamps
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={14} className="text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">Incident Occurred</p>
                    <p className="font-mono text-xs">{new Date(issue.incident_occurred_at).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">System Logged</p>
                    <p className="font-mono text-xs">{new Date(issue.system_logged_at).toLocaleString()}</p>
                  </div>
                </div>
                {issue.notified_at && (
                  <div className="flex items-center gap-2">
                    <Smartphone size={14} className="text-blue-400" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Notified</p>
                      <p className="font-mono text-xs">{new Date(issue.notified_at).toLocaleString()}</p>
                    </div>
                  </div>
                )}
                {issue.first_response_at && (
                  <div className="flex items-center gap-2">
                    <Eye size={14} className="text-emerald-400" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">First Response</p>
                      <p className="font-mono text-xs">{new Date(issue.first_response_at).toLocaleString()}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Set Pending Dialog */}
        <Dialog open={showPendingDialog} onOpenChange={setShowPendingDialog}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Pause className="w-5 h-5 text-purple-400" />
                Set Issue to Pending
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Pending Reason *</label>
                <Select value={pendingReason} onValueChange={setPendingReason}>
                  <SelectTrigger data-testid="pending-reason-select">
                    <SelectValue placeholder="Select reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PENDING_REASONS.map((reason) => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Note (Optional)</label>
                <Textarea
                  placeholder="Add details about why this is pending..."
                  value={pendingNote}
                  onChange={(e) => setPendingNote(e.target.value)}
                  data-testid="pending-note"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPendingDialog(false);
                  setPendingReason("");
                  setPendingNote("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSetPending}
                disabled={pendingLoading || !pendingReason}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="confirm-pending-btn"
              >
                {pendingLoading ? "Setting..." : "Set Pending"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Image Lightbox Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-4xl max-h-[90vh]">
              <Button
                variant="ghost"
                size="sm"
                className="absolute -top-10 right-0 text-white hover:text-white hover:bg-white/20"
                onClick={() => setSelectedImage(null)}
              >
                <X size={20} />
              </Button>
              <img
                src={selectedImage}
                alt="Full size"
                className="max-w-full max-h-[85vh] object-contain rounded"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default IssueDetail;
